import React from "react";
import { Button, Radio, Select } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import QuestionCard from "elements/QuestionCard";
import ButtonV2 from "elements/ButtonV2";

//Make sure this is consistent with getQuestions.tsx if any values are changed
const options = [
	{ label: "Sole Owner", value: "Sole Owner" },
	{ label: "Own it with my spouse", value: "Own With Spouse" },
	{ label: "Other persons", value: "Other Persons" },
];

const Owners = () => {
	const {
		flowId,
		next,
		ownersValue,
		setOwnersValue,
		specialFieldsValue,
		setSpecialFieldsValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Ownership_Answer,
			data: {
				"Ownership ?": ownersValue,
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		const trackingEventData = {
			Ownership: ownersValue,
		};
		next({ trackingEventData });
	};

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setOwnersValue(e.target.value);

		if (
			specialFieldsValue?.income_tax_history?.[0]?.owner1?.filing_status ===
				"Married filing jointly" &&
			e.target.value === "Own With Spouse"
		) {
			setSpecialFieldsValue({
				income_tax_history: [
					{
						...specialFieldsValue?.income_tax_history?.[0],
						owner2: {
							filing_status: "Married filing jointly",
						},
					},
				],
			});
		}
	};

	const body = React.useMemo(() => {
		if (flowId === "GEORGIA_EXEMPTION") {
			return (
				<div className="property-profile-question-card">
					<div className="property-profile-question-card-top">
						<div className="property-profile-question-card-title-container">
							<p>Are you the sole owner of this property?</p>
							<div className="property-profile-question-card-radio">
								<Radio
									name="ownersValue"
									checked={ownersValue === "Sole Owner"}
									onChange={e => {
										setOwnersValue("Sole Owner");
									}}
									value="Yes"
									label="Yes"
								/>
								<Radio
									name="ownersValue"
									checked={ownersValue === "Other Persons"}
									onChange={e => {
										setOwnersValue("Other Persons");
									}}
									value="No"
									label="No"
								/>
							</div>
						</div>
						<p className="sm property-profile-question-card-subtitle">
							The county requires information be provided for all owners listed
							on the deed.
						</p>
					</div>
				</div>
			);
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return (
				<div className="property-profile-question-card">
					<div className="property-profile-question-card-top">
						<div className="property-profile-question-card-title-container">
							<p>Are you the sole owner of this residence?</p>
							<div className="property-profile-question-card-radio">
								<Radio
									name="ownersValue"
									checked={ownersValue === "Sole Owner"}
									onChange={e => {
										setOwnersValue("Sole Owner");
									}}
									value="Yes"
									label="Yes"
								/>
								<Radio
									name="ownersValue"
									checked={ownersValue === "Other Persons"}
									onChange={e => {
										setOwnersValue("Other Persons");
									}}
									value="No"
									label="No"
								/>
							</div>
						</div>
						<p className="sm property-profile-question-card-subtitle">
							This would be all names listed on the property's current Deed.
						</p>
					</div>
				</div>
			);
		}

		return (
			<>
				<div className="property-profile-question-card exemption-survey-card">
					<div className="property-profile-question-card-title-container">
						<p>
							Are you the sole owner, or are there multiple owners of this
							residence?
						</p>
					</div>
					<p className="exemption-question-card-subtitle sm">
						This would be all names listed on the property's current Deed.
					</p>
					<div className="mt-2" />
					<Select
						label="Ownership Information"
						required
						value={ownersValue ?? ""}
						onChange={handleChange}>
						<option value="" hidden disabled>
							Select an Answer
						</option>
						{options.map(({ label, value }) => (
							<option key={label} value={value}>
								{label}
							</option>
						))}
					</Select>
				</div>
				{ownersValue === "Sole Owner" &&
					details?.county?.toLowerCase() === "fort bend" &&
					details?.state === "TX" && (
						<p className="sm exemptions-notification-message-azure">
							Your county requires that all owners on the Deed of the property
							are listed as applicants for the exemption. To ensure your
							application is processed without any issues, please be sure you've
							answered this question accurately.
						</p>
					)}
			</>
		);
	}, [ownersValue, setOwnersValue]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			{body}
			<ButtonV2 size="large" disabled={!ownersValue} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default Owners;
